// Tool definition extraction from a parsed request (parseApiRequest output's
// `toolDefs`).
//
// The per-tool analog of mcpServerDefs.js's server-surface extraction, but
// covering EVERY offered tool (built-in AND MCP) with no server grouping — so a
// built-in like `Bash` gets a definition + drift surface too. Produces the
// `tool` / `tool.fingerprint` signal tags the backend `tool` signal emits; this
// is the JS original the `toolExtractor` kernel is parity-checked against.
//
// Self-contained on purpose (its own hash/normalize/stable-json helpers, mirroring
// skillBodies.js / memoryBodies.js): the `tool` signal owns its module and shares
// no code with `mcp_server` — the two are sibling signals over the same tool_defs,
// not one built on the other.

const normWs = (s) => String(s || "").replace(/\s+/g, " ").trim();

// Canonical JSON with object keys sorted at every level, so two schemas that
// differ only in key order fingerprint identically (we flag genuine definition
// changes, not serialization noise).
function stableJson(v) {
    if (v == null) return "";
    if (typeof v !== "object") return JSON.stringify(v);
    if (Array.isArray(v)) return `[${v.map(stableJson).join(",")}]`;
    return `{${Object.keys(v)
        .sort()
        .map((k) => `${JSON.stringify(k)}:${stableJson(v[k])}`)
        .join(",")}}`;
}

// FNV-1a 64-bit content digest as zero-padded 16-hex, mirroring the backend
// `tool` signal's _content_hash (BigInt keeps the 64-bit wraparound exact). Same
// algorithm on both sides; note a whole-number-float schema literal serializes as
// "1" here (JSON.parse collapses 1.0→1) but "1.0" under Python's json, so an FE
// digest is not guaranteed byte-equal to the durable facet for such literals. The
// tool lens never mixes the two — it groups defs by a digest it computes here, so
// grouping stays self-consistent regardless.
function contentHash(text) {
    let h = 0xcbf29ce484222325n;
    for (const b of new TextEncoder().encode(String(text ?? ""))) {
        h = ((h ^ BigInt(b)) * 0x100000001b3n) & 0xffffffffffffffffn;
    }
    return h.toString(16).padStart(16, "0");
}

// The drift key for one tool DEFINITION: FNV-1a of normWs(description) + unit-sep
// + stableJson(schema). Equal digests = same definition; differing across the
// fleet / over time = drift (version skew or tampering). Accepts the {description,
// schema} shape of both a raw tool_def and a parsed `tool` signal value, so the
// tool lens can group the durable defs it reads into variants with the SAME key
// the `tool.fingerprint` facet carries — no separate fingerprint fetch to join.
export function toolFingerprint(def) {
    const description = def && typeof def.description === "string" ? def.description : "";
    const schema = def ? def.schema ?? null : null;
    return contentHash(`${normWs(description)}␟${stableJson(schema)}`);
}

// All-tools definition extraction — every offered tool (no mcp__ filter).
// Produces the `tool` + `tool.fingerprint` signal tags: dedup by name; instance
// value = {name, description, schema}; the fingerprint facet is toolFingerprint —
// the drift key.
export function extractToolDefs(toolDefs) {
    const tags = [];
    const seen = new Set();
    for (const t of toolDefs || []) {
        const name = t && t.name;
        if (typeof name !== "string" || !name || seen.has(name)) continue;
        seen.add(name);
        const description = typeof t.description === "string" ? t.description : "";
        const schema = t.schema ?? null;
        tags.push({ subject_type: "endpoint", namespace: "tool", tag: name, value: JSON.stringify({ name, description, schema }) });
        tags.push({ subject_type: "endpoint", namespace: "tool.fingerprint", tag: name, value: toolFingerprint({ description, schema }) });
    }
    return tags;
}
